import React, { useState } from "react";

import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import { SignInButton } from "@hl/shared-features/lib/features/auth/SignInButton";
import { useSignOut } from "@hl/shared-features/lib/features/auth/hooks/useSignOut";
import {
  GateConditionsPanel,
  ThisEntityLocked,
  ThisEntityUnlocked,
} from "@hl/shared-features/lib/features/gate/GateConditionsModalBody";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import DescriptionMarkdown from "@hl/shared-features/lib/features/markdown/DescriptionMarkdown";
import {
  Button,
  Center,
  createStyles,
  Flex,
  Group,
  Paper,
  Skeleton,
  Space,
  Text,
  UnstyledButton,
  DefaultProps,
  Selectors,
  Box,
  Stack,
} from "@mantine/core";

import { getMintPageUrl, MINT_BASE_URL } from "~config";
import { GenArtHeaderImported } from "~features/MintPage/GenArtHeaderImported";
import { useMintPage } from "~hooks/useMintPage";
import { useMintPageTokens } from "~hooks/useMintPageTokens";
import { MintContext } from "~hooks/useMintState";
import { MintTokensContext } from "~hooks/useMintTokensState";

import {
  _CollectionType,
  CollectionContractType,
  MintVectorStatus,
  PriceType,
} from "../../apollo/graphql.generated";
import DisabledMintBanner from "../browse/DisabledMintBanner";
import useReferral from "../share/useReferral";

import { AuctionCardInner } from "./Auction/AuctionCard";
import { AuctionCardDate } from "./Auction/AuctionCardDate";
import { SaleStatusAuction } from "./Auction/types";
import { CreatedBy, EditionOf } from "./CollectionInfo";
import { GenArtHeader } from "./GenArtHeader";
import CollectorsStats from "./MintVector/CollectorsStats";
import { MintCardInner } from "./MintVector/MintCard";
import MintCardDate from "./MintVector/MintCardDate";
import { SHLOMS_404_FALLBACK_IMG_URL } from "./custom/Shloms404";
import { RebateCollectorButton } from "./dutch-auction/RebateCollectorModal";
import CardAnimation from "./embed/CardAnimation";
import CardGateStatus from "./embed/CardGateStatus";
import InnerCard from "./embed/InnerCard";
import MainImage, { IMAGE_FIT_SIZE } from "./embed/MainImage";
import { SeriesTokensPanel } from "./embed/SeriesPanel";
import { useEmbedFlags } from "./embed/customize";
import { useMintEmbedCommandListeners } from "./embed/embed-commands";
import { isVideoUrl } from "./header/MintSeriesHeader";
import { GetSalePageMintVectorsDocument } from "./queries.graphql.generated";
import { RankedAuctionCards } from "./ranked-auction/RaMintCard";

import { MintPageDetailSection } from ".";

const useStyles = createStyles((theme) => ({
  root: {
    position: "relative",
    minWidth: 320,
    maxWidth: 400,
    backgroundColor: theme.colors.primaryBackground[0],
    borderRadius: theme.radius.md,
  },
  video: {
    width: "100%",
    display: "block",
  },
  audioButton: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 10,
    width: 70,
    height: 70,
    backgroundColor: theme.colors.primaryBackground[0],
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

type EmbedCardStylesNames = Selectors<typeof useStyles>;

// style names that can be set from the embed holder
// it's our public api, careful with removing
export const EMBED_CARD_STYLE_NAMES: EmbedCardStylesNames[] = ["root"];

export interface EmbedCardProps extends DefaultProps<EmbedCardStylesNames> {
  collectionId: string;
  containerHtml?: string;
}

type SeriesImagesProps = {
  urls: string[];
};

const SeriesImg = ({ src, ml }: { src: string; ml?: number }) => (
  <ResizedImage fit="cover" fitSize={200} height="100%" src={src} ml={ml} />
);

const SeriesImages = ({ urls: images }: SeriesImagesProps) => {
  const urls = images.filter((x) => !isVideoUrl(x));

  if (urls.length < 4) {
    return <MainImage src={urls[0]} hasGate={false} />;
  }
  // add empty items that are not used
  if (urls.length < 4) {
    urls.splice(1, 0, "", "");
  }
  if (urls.length < 5) {
    urls.splice(1, 0, "");
  }
  return (
    <>
      <Flex>
        <SeriesImg src={urls[2]} />
        <SeriesImg src={urls[3]} ml={4} />
      </Flex>
      <Flex mt={4}>
        <SeriesImg src={urls[0]} />
        <SeriesImg src={urls[4]} ml={4} />
      </Flex>
    </>
  );
};

export const MintCardEmbed = ({
  collectionId,
  classNames,
  styles,
  unstyled,
  containerHtml,
}: EmbedCardProps) => {
  const { referrerQueryParamValue: referrer } = useReferral();
  const mintPageHook = useMintPage(collectionId, { referrer });
  const {
    mintVector,
    mintVectorsLoading,
    collectionLoading,
    creatorAddresses,
    handleMint,
    contentTypeInfo,
    collection,
    tokens,
    auction,
    auctionsLoading,
    isSeriesMint,
    isCodeGenMint,
    isCollectorChoiceMint,
    disabledCardText,
    auctionStatus,
    eligibleRebate,
    isPossibleRebate,
    isImported,
    isMarketplaceEnabledForCollectionChain,
    setNumTokensToMint,
    setMintData,
    setDonationAmount,
  } = mintPageHook;

  const mintPageTokensHook = useMintPageTokens(
    collectionId,
    collection?.marketplaceId,
    collection?.entityId ?? collection?.editionId,
    collection?.type === CollectionContractType.Imported,
    isMarketplaceEnabledForCollectionChain,
    collection?.standard,
    true
  );

  useMintEmbedCommandListeners({
    collectionId,
    setNumTokensToMint,
    setMintData,
    setDonationAmount,
  });

  const [showDetails, setShowDetails] = useState(false);
  const [showGateConditions, setShowGateConditions] = useState(false);
  const [showSeriesTokens, setShowSeriesTokens] = useState(false);
  const handleShowGateConditions = () => setShowGateConditions(true);

  const { authenticated } = useAuth();

  const hasGate = !!mintVector?.userGateAccess;
  const { classes } = useStyles(undefined, {
    name: "MintCardEmbed",
    classNames,
    styles,
    unstyled,
  });
  const signOut = useSignOut();
  const flags = useEmbedFlags();
  const isButtonOnly = flags.isButtonOnly;

  if (mintVectorsLoading || auctionsLoading || collectionLoading) {
    if (flags.useContentAsLoading && containerHtml) {
      return <div dangerouslySetInnerHTML={{ __html: containerHtml }} />;
    }
    if (isButtonOnly) {
      return <Button loading fullWidth size="xl" />;
    }
    return (
      <div className={classes.root}>
        <Skeleton h={400} />
      </div>
    );
  }
  if (!collection) {
    return <div>Mint card error: not found</div>;
  }
  const token = tokens[0];
  const title = collection.name || "";

  const userGateAccess = mintVector?.userGateAccess ?? auction?.userGateAccess;
  const isLocked = (userGateAccess && !userGateAccess.passed) ?? false;

  const status = mintVector?.mintVectorStats?.status;

  const isActive = mintVector
    ? status == MintVectorStatus.InProgress
    : auction &&
      [SaleStatusAuction.ACTIVE, SaleStatusAuction.ACTIVE_WITH_BID].includes(
        auctionStatus
      );
  const entity = auction ? "auction" : "mint";
  const creatorEns = collection.creatorEns;

  const marketplaceUrl = getMintPageUrl(collection, {
    marketplace: true,
    withBaseUrl: true,
  });

  const visibility = showDetails || showGateConditions ? "hidden" : "visible";

  const isDutchAuction = mintVector?.priceType === PriceType.DutchAuction;
  const isRankedAction = mintVector?.priceType === PriceType.RankedAuction;
  const isFixedPrice = mintVector?.priceType === PriceType.Fixed;
  const imgOnlyMode = flags.imgOnlyWhenDisabled && !!disabledCardText;
  const hideButtons =
    (isDutchAuction && flags.hideDaButtons) ||
    (isRankedAction && flags.hideRaButtons) ||
    (isFixedPrice && flags.hideFpButtons) ||
    (auction && flags.hideAuctionButtons);

  if (isButtonOnly) {
    return (
      <MintContext.Provider value={mintPageHook}>
        <MintTokensContext.Provider value={mintPageTokensHook}>
          <MintCardInner
            onMintClick={handleMint}
            hideMintButton={
              hideButtons ||
              (authenticated && isLocked) ||
              isCollectorChoiceMint
            }
            statsStyles={{
              value: {
                fontSize: 24,
              },
            }}
            hideCollectorStats
            isButtonOnly
            hideCardStats={
              flags.hidePrice ||
              (isDutchAuction && flags.hidePriceDutchAuction) ||
              (isFixedPrice && flags.hidePriceFixed)
            }
          />
        </MintTokensContext.Provider>
      </MintContext.Provider>
    );
  }

  return (
    <MintContext.Provider value={mintPageHook}>
      <MintTokensContext.Provider value={mintPageTokensHook}>
        <Paper className={classes.root} p="md" shadow="md">
          <div style={{ visibility }}>
            {!flags.hideMintCloseDate && !isRankedAction && (
              <>
                {mintVector && <MintCardDate />}
                {auction && <AuctionCardDate />}
              </>
            )}

            {!flags.hideArt && (
              <>
                <Space h={14} />
                {token.animation_url ? (
                  <CardAnimation
                    animation={token.animation_url}
                    contentTypeInfo={contentTypeInfo}
                    image={token.image}
                    hasGate={hasGate}
                  />
                ) : isSeriesMint &&
                  collection.seriesImages &&
                  collection.baseUri ? (
                  <SeriesImages urls={collection.seriesImages.urls ?? []} />
                ) : isCodeGenMint ? (
                  isImported ? (
                    <GenArtHeaderImported collection={collection} isEmbed />
                  ) : (
                    <GenArtHeader collection={collection} isEmbed />
                  )
                ) : (
                  <MainImage
                    src={
                      isCodeGenMint
                        ? collection.generativeDetails?.logoUrl || ""
                        : token.image
                    }
                    hasGate={hasGate}
                  />
                )}
              </>
            )}
            {hasGate && isActive && !hideButtons && (
              <CardGateStatus
                entity={entity}
                isLocked={isLocked}
                isCodeGenMint={isCodeGenMint}
                onShowDetails={handleShowGateConditions}
              />
            )}

            {!flags.hideTitle ? (
              <>
                <Text weight={500} size={32} pt={!flags.hideArt ? "md" : 0}>
                  {title}
                </Text>
                {!flags.hideDetails && (
                  <Text color="secondaryText" size="sm" mb="md">
                    <Group spacing="sm">
                      <CreatedBy
                        creatorAddresses={creatorAddresses}
                        creatorEns={creatorEns}
                      />
                      {collection.type !== CollectionContractType.General && (
                        <>
                          <span>·</span>
                          {isSeriesMint ? (
                            <span>Series</span>
                          ) : isCodeGenMint ? (
                            <span>Generative Series</span>
                          ) : (
                            <EditionOf editionSize={tokens[0].size} />
                          )}
                        </>
                      )}
                    </Group>
                    {token.nonTransferable && <span>Non-transferable</span>}
                  </Text>
                )}
              </>
            ) : (
              !imgOnlyMode && <Space h={16} />
            )}

            {mintVector && !disabledCardText && !isRankedAction && (
              <MintCardInner
                onMintClick={handleMint}
                hideMintButton={
                  hideButtons ||
                  (authenticated && isLocked) ||
                  isCollectorChoiceMint
                }
                statsStyles={{
                  value: {
                    fontSize: 24,
                  },
                }}
                hideCollectorStats
                hideCardStats={
                  flags.hidePrice ||
                  (isDutchAuction && flags.hidePriceDutchAuction) ||
                  (isFixedPrice && flags.hidePriceFixed)
                }
              />
            )}
            {isRankedAction && !flags.hideRaButtons && (
              <Stack spacing="md">
                <RankedAuctionCards onMintClick={() => void 0} />
              </Stack>
            )}
            {mintVector && disabledCardText && !imgOnlyMode && !isRankedAction && (
              <DisabledMintBanner
                disabledCardText={disabledCardText}
                marketplaceUrl={marketplaceUrl}
                marketplaceButtonProps={{
                  fullWidth: true,
                }}
                collectionId={collection.id}
                collectionName={collection.name}
              />
            )}
            {imgOnlyMode && !authenticated && isDutchAuction && !hideButtons && (
              /* Dutch action may be pending rebate, that's why it's important to have "Connect Wallet" in sold out state */
              <Box pt={15}>
                <SignInButton fullWidth />
              </Box>
            )}
            {disabledCardText && eligibleRebate && !hideButtons && (
              <Box pt={8}>
                <RebateCollectorButton eligibleRebate={eligibleRebate} />
              </Box>
            )}
            {isPossibleRebate && (
              <Text
                size="xs"
                align="center"
                pt={8}
                color={TEXT_COLOR.SECONDARY}
              >
                You can claim your rebate once the resting price is reached
              </Text>
            )}
            {auction && !flags.hidePrice && (
              <>
                <AuctionCardInner />
                {/*
              <Space h="xl" />
               <AuctionHistory
                auction={auction}
                creatorAddresses={creatorAddresses}
              /> */}
              </>
            )}

            {isLocked && isActive && !hideButtons && (
              <Button
                mt={8}
                color="gray.0"
                fullWidth
                onClick={handleShowGateConditions}
              >
                View requirements to unlock
              </Button>
            )}
            {isCollectorChoiceMint && !disabledCardText && (
              <Button
                mt={8}
                fullWidth
                onClick={() => setShowSeriesTokens(true)}
              >
                Select Token
              </Button>
            )}
            {!imgOnlyMode && !hideButtons && <Space h="sm" />}
            {!flags.hideCollectionLinks && (
              <Center>
                <UnstyledButton onClick={() => setShowDetails(true)}>
                  <Text color="secondaryText" size="sm">
                    Collection details
                  </Text>
                </UnstyledButton>
                <Text px="sm" color="secondaryText">
                  {"·"}
                </Text>
                <UnstyledButton
                  component="a"
                  href={`${MINT_BASE_URL}/${collection.id}`}
                  target="_blank"
                >
                  <Text color="secondaryText" size="sm">
                    View{" "}
                    {collection.collectionType === _CollectionType.Series
                      ? "series"
                      : ""}{" "}
                    on Highlight
                  </Text>
                </UnstyledButton>
              </Center>
            )}

            <Center>
              {authenticated && !hideButtons && (
                <div>
                  {imgOnlyMode && disabledCardText && <Space h={8} />}
                  <UnstyledButton onClick={signOut}>
                    <Text color="secondaryText" size="sm">
                      Sign Out
                    </Text>
                  </UnstyledButton>
                </div>
              )}
            </Center>
            {mintVector &&
              !imgOnlyMode &&
              !flags.hideStats &&
              !isRankedAction &&
              (!isDutchAuction || !flags.hideStatsDutchAuction) &&
              (!isFixedPrice || !flags.hideStatsFixedPrice) && (
                <CollectorsStats />
              )}
          </div>
          {showDetails && (
            <InnerCard onClose={() => setShowDetails(false)} title="Details">
              <Group spacing="sm">
                <ResizedImage
                  src={
                    collection.flagVariations.shloms404UI
                      ? SHLOMS_404_FALLBACK_IMG_URL
                      : token.image ||
                        collection.seriesImages?.coverImageUrl ||
                        collection.generativeDetails?.logoUrl
                  }
                  fitSize={IMAGE_FIT_SIZE}
                  width={56}
                  height={56}
                  radius="sm"
                />
                <div>
                  <Text size={24}>{title}</Text>
                  <Text color="secondaryText" size="sm">
                    <Group>
                      <CreatedBy
                        creatorAddresses={creatorAddresses}
                        creatorEns={creatorEns}
                        copyableAddress
                      />
                    </Group>
                  </Text>
                </div>
              </Group>
              {token.description && (
                <DescriptionMarkdown description={token.description} />
              )}
              <Text my="md" weight="bolder" size="sm">
                Details
              </Text>
              <MintPageDetailSection token={tokens[0]} />
            </InnerCard>
          )}
          {showGateConditions && userGateAccess && (
            <InnerCard
              onClose={() => setShowGateConditions(false)}
              title={
                isLocked ? (
                  <ThisEntityLocked entity={entity} />
                ) : (
                  <ThisEntityUnlocked entity={entity} />
                )
              }
              bottomArea={
                isLocked && !authenticated && <SignInButton fullWidth />
              }
            >
              <GateConditionsPanel
                result={userGateAccess}
                isPreview={false}
                creatorAddress={(creatorAddresses ?? [])[0].address}
                creatorEns={creatorEns}
                refetchOnRequirementChange={[GetSalePageMintVectorsDocument]}
              />
            </InnerCard>
          )}
          {showSeriesTokens && (
            <SeriesTokensPanel
              collectionId={collectionId}
              onMintClick={handleMint}
              onClose={() => setShowSeriesTokens(false)}
            />
          )}
        </Paper>
      </MintTokensContext.Provider>
    </MintContext.Provider>
  );
};
